/**
 * Dashboard Page Component
 * Main dashboard showing overview statistics and key metrics
 * Matches the Claude artifact HTML structure exactly
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../store/AdminContext';
import { apiService } from '../services/apiService';
import './DashboardPage.css';

const DashboardPage: React.FC = () => {
  const { setCurrentPanel, setCurrentPage } = useAdmin();
  const [totalUsers, setTotalUsers] = useState<string>('0');
  const [verifiedUsers, setVerifiedUsers] = useState<string>('0');
  const [pendingKYC, setPendingKYC] = useState<string>('0');
  const [bannedFrozen, setBannedFrozen] = useState<string>('0');
  const [pendingPayouts, setPendingPayouts] = useState<string>('0');
  const [volume24h] = useState<string>('—');
  const [activeMarkets, setActiveMarkets] = useState<string>('0');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {

      // Fetch all users to get counts
      const allUsersData = await apiService.getAllUsers({});
      const allUsers = allUsersData.users || [];

      // Calculate user statistics
      const total = allUsers.length;
      const verified = allUsers.filter(u => u.email_verified && !u.deleted_at && !u.frozen_at).length;
      const pending = allUsers.filter(u => !u.email_verified && !u.deleted_at && !u.frozen_at).length;
      // Banned/Frozen includes both deleted and frozen users
      const banned = allUsers.filter(u => u.deleted_at || u.frozen_at).length;

      setTotalUsers(total.toLocaleString());
      setVerifiedUsers(verified.toLocaleString());
      setPendingKYC(pending.toLocaleString());
      setBannedFrozen(banned.toLocaleString());

      // Fetch pending withdrawals count
      try {
        const { withdrawals } = await apiService.getWithdrawals('pending');
        setPendingPayouts((withdrawals?.length ?? 0).toLocaleString());
      } catch {
        setPendingPayouts('0');
      }

      // Fetch active markets
      const marketsData = await apiService.getAllMarkets({ status: 'live' });
      const activeMarketsCount = marketsData.markets?.length || 0;
      setActiveMarkets(activeMarketsCount.toLocaleString());
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  };

  // TODO: Replace with real API calls to get recent activity
  const recentActivity: Array<{ icon: string; action: string; admin: string; target: string; time: string; type: string }> = [];

  // TODO: Replace with real API calls to get risk cases
  const riskCases: Array<{ id: string; type: string; users: string[]; created: string; severity: string }> = [];

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export clicked');
  };

  const handleCreateMarket = () => {
    setCurrentPage('predictions-overview');
  };

  const goToPayouts = () => {
    setCurrentPanel('finance');
    setCurrentPage('payout-queue');
  };

  const goToRisk = () => {
    setCurrentPanel('risk');
    setCurrentPage('risk-cases');
  };

  const goToTickets = () => {
    setCurrentPanel('support');
    setCurrentPage('tickets-all');
  };

  const goToLimitRequests = () => {
    setCurrentPanel('compliance');
    setCurrentPage('limit-requests');
  };

  return (
    <div className="dashboard-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-sub">Complete platform overview</p>
        </div>
        <div className="btn-group">
          <button className="btn btn-secondary" onClick={handleExport}>
            📊 Export
          </button>
          <button className="btn btn-primary" onClick={handleCreateMarket}>
            ➕ Create Market
          </button>
        </div>
      </div>

      <div className="stats">
        <div className="stat clickable" onClick={() => setCurrentPage('users-all')}>
          <div className="stat-icon blue">👥</div>
          <div className="stat-val">{totalUsers}</div>
          <div className="stat-label">Total Users</div>
        </div>
        <div className="stat clickable" onClick={() => setCurrentPage('users-verified')}>
          <div className="stat-icon green">✅</div>
          <div className="stat-val">{verifiedUsers}</div>
          <div className="stat-label">Verified Users</div>
        </div>
        <div className="stat clickable" onClick={() => setCurrentPage('users-pending')}>
          <div className="stat-icon orange">⏳</div>
          <div className="stat-val">{pendingKYC}</div>
          <div className="stat-label">Pending KYC</div>
        </div>
        <div className="stat clickable" onClick={() => setCurrentPage('users-banned')}>
          <div className="stat-icon red">🚫</div>
          <div className="stat-val">{bannedFrozen}</div>
          <div className="stat-label">Banned/Frozen</div>
        </div>
        <div className="stat" title="Metric coming soon">
          <div className="stat-icon purple">💰</div>
          <div className="stat-val">{volume24h}</div>
          <div className="stat-label">Volume (24h)</div>
        </div>
        <div className="stat clickable" onClick={() => setCurrentPage('predictions-overview')}>
          <div className="stat-icon cyan">🎯</div>
          <div className="stat-val">{activeMarkets}</div>
          <div className="stat-label">Active Predictions</div>
        </div>
      </div>

      <div className="quick">
        <button className="quick-btn" onClick={() => setCurrentPage('users-pending')}>
          ⏳ KYC Queue ({pendingKYC})
        </button>
        <button className="quick-btn" onClick={goToRisk}>
          🚨 Risk Cases (0)
        </button>
        <button className="quick-btn" onClick={goToPayouts}>
          💸 Payouts ({pendingPayouts})
        </button>
        <button className="quick-btn" onClick={goToTickets}>
          🎫 Tickets (0)
        </button>
        <button className="quick-btn" onClick={goToLimitRequests}>
          📝 Limit Requests
        </button>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <span className="card-title">📜 Recent Activity</span>
            <button className="btn btn-secondary" onClick={() => setCurrentPage('audit-log')}>
              View All
            </button>
          </div>
          <div className="card-body">
            <div className="timeline-modern">
              {recentActivity.length > 0 ? (
                recentActivity.map((item, index) => (
                  <div key={index} className={`timeline-item ${item.type}`}>
                    <div className="timeline-icon">{item.icon}</div>
                    <div className="timeline-content">
                      <div className="timeline-action">{item.action.replace(/_/g, ' ')}</div>
                      <div className="timeline-meta">{item.admin} → {item.target}</div>
                    </div>
                    <div className="timeline-time">{item.time}</div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                  No recent activity
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <span className="card-title">🚨 Active Risk Cases</span>
          </div>
          <div className="card-body">
            {riskCases.length > 0 ? (
              riskCases.map((riskCase, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: index < riskCases.length - 1 ? '1px solid var(--glass-border)' : 'none', cursor: 'pointer' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{riskCase.id}: {riskCase.type}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{riskCase.users.join(', ')} • {riskCase.created}</div>
                  </div>
                  <span className={`status ${riskCase.severity === 'critical' ? 'red' : riskCase.severity === 'high' ? 'orange' : 'yellow'}`}>
                    {riskCase.severity}
                  </span>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                No active risk cases
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;

